/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/client';
import { bindActionCreators } from 'redux';
import QueryString from 'qs';

import {
    string, func, shape, bool, object,
} from 'prop-types';
import { compose } from 'recompose';
import mbpLogger from 'mbp-logger';
// components
import CategoryPageSkeleton from '../GraphqlSkeletonComponents/CategoryPageSkeleton/CategoryPageSkeleton';
import GraphqlSearchBuilder from './GraphqlSearchBuilder';
// helpers, redux, hooks
import findProductsBySearchTerm from '../../../gql/queries/findProductsBySearchTerm';
import { GRAPHQL_ENV } from '../../../gql';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlags, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent as track } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import findContent from '../../../gql/queries/findContent';
import { getActiveABTests } from '../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import priceRangeFilter from '../../../helpers/priceRangeFilter';
import useCheckedFacets from '../../../helpers/hooks/useCheckedFacets';
import useExperimentServiceAttributes from '../../../helpers/experimentService/useExperimentServiceAttributes';
import findCollectionsBySearchTerm from '../../../gql/queries/findCollectionsBySearchTerm';
import findBlogs from '../../../gql/queries/findBlogs';

const determineProductOptions = ({
    lowToHighBtn, highToLowBtn, facets,
}, priceRangeData, isFacetFilterPriceEnabled, pageNumber, pageSize = 36, isBloomreachBrand, isEsEnabled, noRedirect, isSearchDidYouMean, getExact, suggestedTerm = null, boostValue = null, dynamicRanks = {}) => {
    const { isEnabled, list } = dynamicRanks;
    let productOptions = {
        orderBy: 'BEST_SELLERS',
        pageSize,
        page: pageNumber,
        noRedirect,
    };
    if (isEnabled) {
        if (list?.length) {
            const listModel = list?.[0] || null;
            if (listModel?.orderBy && listModel?.type === 'variant') {
                productOptions.orderBy = listModel?.orderBy;
                if (listModel?.name) {
                    productOptions.modelName = listModel?.name;
                }
            }
        }
    }

    if (highToLowBtn) {
        productOptions = { ...productOptions, orderBy: 'PRICE_DESC' };
    }
    if (lowToHighBtn) {
        productOptions = { ...productOptions, orderBy: 'PRICE_ASC' };
    }
    if (facets?.length) {
        productOptions = {
            ...productOptions,
        };
        let priceRanges = [];
        let newFacetsData = facets;
        if (isFacetFilterPriceEnabled) {
            // first we checked that value is present or not
            if (typeof priceRangeData !== 'undefined' && priceRangeData?.entries?.length > 0) {
                newFacetsData = [];
                const priceRangeFilterData = priceRangeFilter(facets, priceRangeData);
                newFacetsData = priceRangeFilterData.newFacetsData;
                priceRanges = priceRangeFilterData.priceRanges;
            }
            if (priceRanges.length > 0) {
                productOptions.priceRangeFilter = {
                    fieldName: 'all_prices',
                    ranges: priceRanges,
                };
            }
        }
        if (newFacetsData.length > 0) {
            productOptions.facets = newFacetsData?.filter((data) => !data.includes('='));
        }
    }
    if (isSearchDidYouMean) {
        productOptions = { ...productOptions, useFallbackSearch: getExact };
    }

    if (boostValue) {
        /**
         * Adding boost array the boost value.
         */
        productOptions.boosts = [
            {
                field: 'brand_id',
                boost: boostValue?.value,
                value: Number(boostValue?.brand || ''),
            },
        ];
    }

    if (isBloomreachBrand && !isEsEnabled) {
        delete productOptions.page;
        delete productOptions.pageSize;
    }
    if (suggestedTerm) {
        productOptions.suggestedTerm = suggestedTerm;
    }

    return productOptions;
};

const GraphqlSearchContainer = ({
    brand, searchTerm, featureFlags, trackEvent, presentationFamily, location, activeABTests,
}) => {
    /**
     * Getting facets from URL(selected list)
     */
    const isAggregatorFacetErrorResolver = featureFlags['is-facet-throwing-error-resolver-enabled'];
    const ffIsLoadMoreOptimizationEnabled = featureFlags['is-load-more-optimization-enabled'];
    const productsToLoadOnLoadMoreButton = ffIsLoadMoreOptimizationEnabled ? 36 : 12; // number of product when we click on load more button

    let checkedFacetsData = [];
    let locationData = '';
    const getCheckedFacetsData = useCheckedFacets();
    if (isAggregatorFacetErrorResolver) {
        checkedFacetsData = getCheckedFacetsData;
        locationData = location?.search?.includes('?facet=') ? location.search.replace('?facet=', '') : '';
    } else {
        locationData = location?.search?.includes('?facet=') ? location.search.replace('?facet=', '') : '';
        const redirectLocationData = locationData?.split('&');
        checkedFacetsData = redirectLocationData.length && redirectLocationData?.[0] !== '' ? redirectLocationData : [];
    }

    const facetsDataList = {};
    if (checkedFacetsData.length > 0) {
        facetsDataList.facets = checkedFacetsData;
    }
    const searchPageDynamicTest = activeABTests?.search_page_orders || [];
    const isSearchDidYouMean = featureFlags['search-did-you-mean'];
    const [filterOptions, setFilterOptions] = useState(facetsDataList);
    const [priceFilters, setPriceFilters] = useState([]);
    const pageCount = useRef(1);
    const loadMoreCount = useRef(1);
    const hasMounted = useRef(false);
    const [suggestedTerm, setSuggestedTerm] = useState('');
    const { targeting, context, isGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findProductsBySearchTerm' });
    const { targeting: collectionGqlTargeting, context: collectionGqlContext, isGraphqlTargetingEnabled: collectionGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findCollectionsBySearchTerm' });
    const SEARCH_PAGE_QUERY = findProductsBySearchTerm(isGraphqlTargetingEnabled, context?.api === 'experiments');
    const SEARCH_PAGE_FACETS_QUERY = findProductsBySearchTerm(isGraphqlTargetingEnabled, context?.api === 'experiments');
    const SEARCH_PAGE_COLLECTION_QUERY = findCollectionsBySearchTerm(collectionGraphqlTargetingEnabled);
    const SEARCH_PAGE_ARTICLE_QUERY = findBlogs();
    const newSearchTerm = searchTerm.replace(/[&\\#,-,!,+()$~%.'":*?<>{}]/g, ' '); // replacing special chars allowing and add the space
    const [getSearchFacets, {
        data: searchFacetData,
    }] = useLazyQuery(SEARCH_PAGE_FACETS_QUERY);

    const [getSearchProducts, {
        loading, error, data, fetchMore, refetch, variables,
    }] = useLazyQuery(SEARCH_PAGE_QUERY);

    const [getSearchCollections, {
        data: collectionData,
    }] = useLazyQuery(SEARCH_PAGE_COLLECTION_QUERY);

    const [getSearchArticles, {
        data: articleData,
    }] = useLazyQuery(SEARCH_PAGE_ARTICLE_QUERY);

    const isSearchFacetFromSeparateQuery = featureFlags['is-search-facet-from-separate-query-enabled'];

    useEffect(() => {
        if (isSearchFacetFromSeparateQuery) {
            const [priceRangeData] = (searchFacetData?.findProductsBySearchTerm?.productFacets || [])?.filter((value) => value?.name?.toLowerCase() === 'price range');
            setPriceFilters(priceRangeData);
        }
    }, [searchFacetData?.findProductsBySearchTerm?.productFacets, isSearchFacetFromSeparateQuery]);

    useEffect(() => {
        if (!isSearchFacetFromSeparateQuery) {
            const [priceRangeData] = (data?.findProductsBySearchTerm?.productFacets || [])?.filter((value) => value?.name?.toLowerCase() === 'price range');
            setPriceFilters(priceRangeData);
        }
    }, [data?.findProductsBySearchTerm?.productFacets, isSearchFacetFromSeparateQuery]);

    const isBloomreachBrand = brand?.['bloomreach-account-id'];
    const isEsEnabled = featureFlags['is-elastic-search-enabled'];
    const areDeliveryTypeFilterOptionsShown = featureFlags['is-delivery-type-filter-options-enabled'];

    let searchResults = {};
    let isSearchTermBeingABTested = !!activeABTests?.searchTest?.[searchTerm];
    // passing noRedirect true for food if flag is enabled!
    if (presentationFamily === 'food' && featureFlags['is-search-no-redirect-enabled']) {
        isSearchTermBeingABTested = true;
    }

    let boostValue = null;
    let isControlVariant = false;

    const isBoostEnable = featureFlags['is-search-boost-enabled'];
    const isFacetFilterPriceEnabled = featureFlags['is-facet-filter-price-enabled'];
    if (isBoostEnable) {
        if (activeABTests?.searchVariant?.type?.toLowerCase?.() === 'control') {
            isControlVariant = true;
            boostValue = {
                variant: activeABTests?.searchVariant?.type?.toLowerCase?.(),
                value: (!Number?.isNaN?.(activeABTests?.searchVariant?.boostValue)) ? activeABTests?.searchVariant?.boostValue : 3999,
                brand: activeABTests?.searchVariant?.boostBrand || brand?.id,
            };
        } else if (activeABTests?.searchVariant?.type?.toLowerCase?.() === 'variant') {
            boostValue = {
                variant: activeABTests?.searchVariant?.type?.toLowerCase?.(),
                value: activeABTests?.searchVariant?.boostValue || 0,
                brand: activeABTests?.searchVariant?.boostBrand || brand?.id,
            };
        }
    }

    const getExact = QueryString.parse(location.search.replace('?', ''))?.exact !== '1';
    useEffect(() => {
        if (isSearchFacetFromSeparateQuery) {
            getSearchFacets({
                variables: {
                    brand: brand['domain-name'],
                    term: searchTerm,
                    productOptions: {}, // avoiding facets to update based on options, it only render once based on above search term
                    environment: GRAPHQL_ENV,
                    ...(isGraphqlTargetingEnabled ? { targeting } : {}),
                },
                context,
            });
        }
    }, []);

    useEffect(() => {
        if (featureFlags['is-collections-on-search-page-enabled']) {
            getSearchCollections({
                variables: {
                    brand: brand['domain-name'],
                    term: searchTerm,
                    collectionOptions: {
                        pageSize: 4,
                        page: 0,
                    },
                    environment: GRAPHQL_ENV,
                    ...(collectionGraphqlTargetingEnabled ? { targeting: collectionGqlTargeting } : {}),
                },
                context: collectionGqlContext,
            });
        }
    }, []);

    useEffect(() => {
        if (featureFlags['is-articles-on-search-page-enabled']
            && featureFlags['article-brand-contentype-map']
            && featureFlags['article-brand-contentype-map']?.[brand['domain-name']]
        ) {
            getSearchArticles({
                variables: {
                    contentType: `${featureFlags['article-brand-contentype-map'][brand['domain-name']]}_posts`,
                    blogOptions: {
                        keyword: searchTerm,
                        page: 0,
                        pageSize: 4,
                    },
                    environment: GRAPHQL_ENV,
                },
            });
        }
    }, []);

    const fetchMoreResults = (productsToLoad = productsToLoadOnLoadMoreButton) => {
        if (fetchMore) {
            // the below condiitonal is used to mathematically determine the current page.
            // it is necessary to do this because of apollo's caching.
            // if the user searches and paginates then clicks on a product and then goes back to that search
            // then they scroll to paginate again apollo will return all of the previously fetched products as part of that first
            // search query because they are all in apollo's cache.
            // this can lead to an issue where the pageCount is 3 becasue it's after the first pagination call but apollo is returning
            // more products than the first three pages. To prevent this the actual page number is calculated based on number of
            // products that are currently displayed
            if (data?.findProductsBySearchTerm?.products?.length) {
                pageCount.current = Math.ceil(data?.findProductsBySearchTerm?.products?.length / productsToLoad) + 1;
            } else {
                pageCount.current += 1;
            }
            fetchMore({
                variables: {
                    // only need to define the variables that change in between queries
                    productOptions: determineProductOptions(filterOptions, priceFilters, isFacetFilterPriceEnabled, pageCount.current, productsToLoad, isBloomreachBrand, isEsEnabled, isSearchTermBeingABTested, isSearchDidYouMean, getExact, suggestedTerm, boostValue, { list: searchPageDynamicTest, keyword: newSearchTerm, isEnabled: featureFlags['is-keyword-search-dynamic-rank-enabled'] }),
                },
            });
        }
    };
    // this is use for when I click on the load more button.
    // I will show the next LOAD MORE button after the next 36 products,
    // but I load 12, 12, 12 products three times on scroll down.

    const onLoadMoreClick = (productsToLoad = productsToLoadOnLoadMoreButton, isTrue = false) => {
        if (isTrue) {
            loadMoreCount.current += 1;
        }
        fetchMoreResults(productsToLoad);
    };

    const productFacetsGroup = [];
    const contentType = 'template_search_page';
    const query = `\\"search_parameter\\": \\"${newSearchTerm}\\"`;
    const findGetContentTypeData = {
        brand: brand['domain-name'],
        contentType,
        GRAPHQL_ENV,
        query,
        reference: ['filters.filter_ordering.filters'],
    };

    const defaultQuery = {
        brand: brand['domain-name'],
        contentType,
        GRAPHQL_ENV,
        query: '\\"title\\": \\"Default\\"',
        reference: ['filters.filter_ordering.filters'],
    };

    const Search_Content_QUERY = findContent(findGetContentTypeData);
    /**
     * Getting search template data based on search param from CS
     */
    const { loading: searchLoading, error: searchError, data: searchData } = featureFlags['is-search-template-enabled'] && useQuery(Search_Content_QUERY);

    /**
     * Getting Default entry of search template if flag is on.
     */
    const { data: defaultSearchTemplate } = featureFlags['is-search-template-enabled'] && useQuery(findContent(defaultQuery));
    if (!isControlVariant && (searchData || defaultSearchTemplate)) {
        searchResults = searchData?.findContent?.content?.entries?.[0] || defaultSearchTemplate?.findContent?.content?.entries?.[0] || {};
        /**
         * Getting filters based on the search template
         */
        if (isSearchFacetFromSeparateQuery) {
            (searchFacetData?.findProductsBySearchTerm?.productFacets || []).forEach((entries) => {
                productFacetsGroup.push({
                    name: entries.name,
                    entries: entries.entries || [],
                    displayOpen: entries.displayOpen,
                });
            });
        } else {
            (data?.findProductsBySearchTerm?.productFacets || []).forEach((entries) => {
                productFacetsGroup.push({
                    name: entries.name,
                    entries: entries.entries || [],
                    displayOpen: entries.displayOpen,
                });
            });
        }
    }

    useEffect(() => {
        // when this runs on component mount run the lazy query which returns refetch
        // subsequently when this runs on filter props changing use the refetch query
        if (hasMounted.current && typeof refetch !== 'undefined') {
            pageCount.current = 1;
            /**
             * Refetch will use after first load.
             */
            refetch({
                productOptions: determineProductOptions(filterOptions, priceFilters, isFacetFilterPriceEnabled, pageCount.current, productsToLoadOnLoadMoreButton, isBloomreachBrand, isEsEnabled, isSearchTermBeingABTested, isSearchDidYouMean, getExact, suggestedTerm, boostValue, { list: searchPageDynamicTest, keyword: newSearchTerm, isEnabled: featureFlags['is-keyword-search-dynamic-rank-enabled'] }),
                term: searchTerm,
            });
            pageCount.current = 2;
        } else {
            getSearchProducts({
                variables: {
                    brand: brand['domain-name'],
                    term: searchTerm,
                    productOptions: determineProductOptions(filterOptions, priceFilters, isFacetFilterPriceEnabled, pageCount.current, productsToLoadOnLoadMoreButton, isBloomreachBrand, isEsEnabled, isSearchTermBeingABTested, isSearchDidYouMean, getExact, suggestedTerm, boostValue, { list: searchPageDynamicTest, keyword: newSearchTerm, isEnabled: featureFlags['is-keyword-search-dynamic-rank-enabled'] }),
                    environment: GRAPHQL_ENV,
                    ...(isGraphqlTargetingEnabled ? { targeting } : {}),
                },
                context,
            });
            hasMounted.current = true;
        }
    }, [filterOptions, filterOptions?.facets, checkedFacetsData?.length, searchTerm, getExact, searchPageDynamicTest?.length]);

    useEffect(() => {
        if (!loading && data?.findProductsBySearchTerm) {
            if (data.findProductsBySearchTerm.totalProducts === 0) {
                trackEvent({
                    eventCategory: 'Search',
                    eventAction: searchTerm,
                    eventLabel: 'No Results for Term',
                });
            }
        }
    }, [data, loading]);

    useEffect(() => {
        const pageData = data?.findProductsBySearchTerm || '';
        if (pageData && featureFlags['search-did-you-mean']) {
            const misSpelledWord = pageData.originalQuery || null;
            const showingResultFor = pageData.showingResultsFor || null;
            if (misSpelledWord && showingResultFor) {
                trackEvent({
                    eventCategory: '<<pageType>>', eventAction: 'Did you mean - shown', eventLabel: `${showingResultFor}|${misSpelledWord}`,
                });
            }
        }
    }, [data, loading]);

    if (loading || searchLoading) {
        // return <div style={{ height: '80vh' }} />; // prevent header and footer from snapping together
        return <CategoryPageSkeleton />;
    }

    if (error || searchError) {
        const errorLogObj = {
            appName: process.env.npm_package_name,
            function: 'GraphqlSearchContainer.js',
            message: 'Error loading data from Graphql',
        };
        if (searchError) errorLogObj.searchError = searchError;
        if (error) errorLogObj.error = error;
        if (searchTerm) errorLogObj.searchTerm = searchTerm;

        mbpLogger.logError(errorLogObj);
        return null;
    }

    if (!data) {
        const errorLogObj = {
            appName: process.env.npm_package_name,
            function: 'GraphqlSearchContainer.js',
            message: 'No data returned for query',
        };
        if (searchTerm) errorLogObj.searchTerm = searchTerm;

        mbpLogger.logWarning(errorLogObj);
        return null;
    }
    const pageData = data.findProductsBySearchTerm || '';

    if (pageData) {
        trackEvent({
            eventCategory: 'Test Impression', eventAction: !featureFlags['is-elastic-search-enabled'] && 'SOLR', eventLabel: searchTerm,
        });
    }

    if (pageData && featureFlags['search-did-you-mean']) {
        const misSpelledWord = pageData.originalQuery || null;
        const canShowResultsFor = pageData.canShowResultsFor || null;
        if (canShowResultsFor && misSpelledWord) {
            trackEvent({
                eventCategory: '<<pageType>>', eventAction: 'Did you mean - submit', eventLabel: `${misSpelledWord}`,
            });
        }
    }

    if (pageData?.showingResultsFor && suggestedTerm !== pageData.showingResultsFor) {
        setSuggestedTerm(pageData.showingResultsFor);
    }

    return (
        <GraphqlSearchBuilder
            key={location.pathname}
            displayDeliveryMethod={areDeliveryTypeFilterOptionsShown}
            featureFlags={featureFlags}
            fetchMoreResults={onLoadMoreClick}
            loadMoreCount={loadMoreCount.current}
            filterOptions={filterOptions}
            pageCount={pageCount.current}
            pageData={pageData}
            searchResults={searchResults}
            searchTerm={searchTerm}
            setFilterOptions={setFilterOptions}
            track={trackEvent}
            productFacetsGroup={productFacetsGroup}
            collectionData={collectionData?.findCollectionsBySearchTerm}
            articleData={articleData?.findBlogs}
            variables={variables}
            checkedFacetsData={checkedFacetsData}
        />
    );
};

GraphqlSearchContainer.propTypes = {
    brand: shape({
        'domain-name': string.isRequired,
    }).isRequired,
    featureFlags: shape({
        'is-search-template-enabled': bool,
        'is-elastic-search-enabled': bool,
        'is-delivery-type-filter-options-enabled': bool,
        'search-did-you-mean': bool,
        'is-collections-on-search-page-enabled': bool,
        'is-articles-on-search-page-enabled': bool,
        'article-brand-contentype-map': object,
    }),
    searchTerm: string,
    trackEvent: func.isRequired,
    presentationFamily: string.isRequired,
    location: shape({
        pathname: string,
    }),
    activeABTests: object,
};

GraphqlSearchContainer.defaultProps = {
    searchTerm: '',
    featureFlags: {},
    location: {},
    activeABTests: {},
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    featureFlags: getFeatureFlags(state),
    presentationFamily: getPresentationFamily(state),
    activeABTests: getActiveABTests(state),
});

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlSearchContainer);
